/**
 * CRMView.jsx - the Accounts tab shell.
 *
 * Two views: the Accounts list (with live Won/Open/Lost per account) and the
 * Contacts directory. Selecting an account hands off to AccountDetailPanel.
 *
 * The Leads Board used to be a third tab here; it is now its own sidebar tab
 * (views/LeadsView.jsx) so the pipeline reads Leads -> Accounts ->
 * Opportunities -> Projects. Both still render the same crm_accounts rows.
 *
 * Sites are child accounts: they are hidden from this list and its counts, and
 * appear under their parent instead (components/crm/AccountDetailPanel).
 * Neighbours: components/crm/* and utils/customerMatch.js.
 *
 * Deep link: #/crm?account=<id> opens that account's (or site's) panel, on
 * load and on every hash change - the Dashboard's task rows link here.
 * #/crm?contact=<id> opens that contact's page (C-01) the same way; a row of
 * the Contacts directory opens it too. Back clears the parameter again. The
 * directory stays mounted (hidden) under the contact's page, so Back finds
 * its search and filters as they were.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { Icon } from '../components/ui/Icon';
import { Pill } from '../components/ui/Pill';
import { Modal } from '../components/ui/Modal';
import { Toast } from '../components/ui/Toast';
import ImportModal from '../components/ui/ImportModal';
import AccountForm from '../components/forms/AccountForm';
import AiCreateButton from '../components/aiCreate/AiCreateButton';
import { useApp } from '../context/AppContext';
import { canEdit, canDelete } from '../data/roles';
import { useMyAccess } from '../hooks/useMyAccess';
import { fmtDate } from '../utils/format';
import { exportCsv } from '../utils/exportCsv';
import {
  normalizeTier,
  daysInStage,
  designationOf,
  verticalOf,
  leadStatusOf,
  pickField,
  DESIGNATIONS,
  DESIGNATION_DEFINITIONS,
} from '../data/crmStages';
import AccountDetailPanel from '../components/crm/AccountDetailPanel';
import ContactsDirectory from '../components/crm/ContactsDirectory';
import ContactDetailPanel from '../components/crm/ContactDetailPanel';
import { BTN_DELETE, designationTag } from '../components/crm/crmStyles';
import { computeAccountStats } from '../utils/customerMatch';
import NeedsDetailsChip from '../components/crm/NeedsDetailsChip';
import { NotTrackedChip } from '../components/crm/NotTrackedLead';

export { computeAccountStats } from '../utils/customerMatch';

const TIERS = ['All', 'A', 'B', 'C']; // A/B/C off the target scoring model (Paul #39)

/** ?<name>=<value> from the hash (#/crm?account=acct-123, #/crm?contact=con-1), or null. */
function hashParam(name) {
  try {
    const hash = window.location.hash;
    const qIdx = hash.indexOf('?');
    if (qIdx < 0) return null;
    return new URLSearchParams(hash.slice(qIdx + 1)).get(name) || null;
  } catch {
    return null;
  }
}
/** Drop ?<name>= again once the panel is closed, so the same link opens it next time. */
function clearHashParam(name) {
  try {
    const hash = window.location.hash;
    const qIdx = hash.indexOf('?');
    if (qIdx < 0) return;
    const params = new URLSearchParams(hash.slice(qIdx + 1));
    if (!params.has(name)) return;
    params.delete(name);
    const qs = params.toString();
    const next = `${hash.slice(0, qIdx)}${qs ? `?${qs}` : ''}`;
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}${next}`);
  } catch {
    /* hash mutation blocked - ignore */
  }
}
const clearAccountFromHash = () => clearHashParam('account');
const clearContactFromHash = () => clearHashParam('contact');

/* ===== Main CRM View ===== */
export default function CRMView() {
  const { allAccounts, allProjects, deleteAccount, trackRecentlyViewed, fetchAll } = useApp();
  const access = useMyAccess();
  const editable = canEdit(access, 'accounts');
  const deletable = canDelete(access, 'accounts');
  const [topTab, setTopTab] = useState('accounts');
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [selected, setSelected] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null); // contacts.id (C-01)
  const [showForm, setShowForm] = useState(false);
  const [editAccount, setEditAccount] = useState(null);
  const [search, setSearch] = useState('');
  const [tierFilter, setTierFilter] = useState('All');
  // Lead / Account / Customer (Account: Paul, 10/21/26). A company matches when
  // it, or any of its sites, has that designation.
  const [desigFilter, setDesigFilter] = useState('All');
  const [toast, setToast] = useState(null);
  const [showImportAccounts, setShowImportAccounts] = useState(false);
  /* Track recently viewed account */
  useEffect(() => {
    if (selected) trackRecentlyViewed('account', selected.id, selected.name);
  }, [selected, trackRecentlyViewed]);

  // Deep link (#/crm?account=<id> or #/crm?contact=<id>). On a cold load the
  // accounts arrive after the first render, so the link is opened once they
  // do (the ref stops a later refresh from reopening a panel the user already
  // closed). An older link may carry the account's name instead of its id.
  // A contact link opens even when the id is unknown, so the page can say the
  // contact no longer exists instead of silently showing the list.
  const deepLinkHandled = useRef(false);
  useEffect(() => {
    const open = () => {
      const contactKey = hashParam('contact');
      if (contactKey) {
        if (!allAccounts.length) return false; // not loaded yet
        setTopTab('contacts');
        setSelected(null);
        setSelectedContact(contactKey);
        return true;
      }
      const key = hashParam('account');
      if (!key) return false;
      const a = allAccounts.find((x) => x.id === key) || allAccounts.find((x) => String(x.name || '').toLowerCase() === key.toLowerCase());
      if (!a) return false;
      setTopTab('accounts');
      setSelectedContact(null);
      setSelected(a);
      return true;
    };
    if (!deepLinkHandled.current && open()) deepLinkHandled.current = true;
    const onHash = () => {
      open();
    };
    window.addEventListener('hashchange', onHash);
    window.addEventListener('popstate', onHash);
    return () => {
      window.removeEventListener('hashchange', onHash);
      window.removeEventListener('popstate', onHash);
    };
  }, [allAccounts]);

  /* Won / Open / Lost worked out from allProjects on every render, which the
     live updates re-read the moment anyone saves an opportunity - never a
     stored count. */
  const accountStats = useMemo(() => computeAccountStats(allAccounts, allProjects), [allAccounts, allProjects]);
  const getStats = (acc) => accountStats.get(acc.id) || { won: 0, open: 0, lost: 0, cancelled: 0, wonValue: 0, pipelineValue: 0 };
  // companies only: a site's numbers already count on its company
  const topLevel = [...accountStats.entries()]
    .filter(([id]) => !(allAccounts.find((a) => a.id === id) || {}).parentAccountId)
    .map(([, b]) => b);
  const totalWon = topLevel.reduce((s, b) => s + b.won, 0);
  const totalOpen = topLevel.reduce((s, b) => s + b.open, 0);
  const totalLost = topLevel.reduce((s, b) => s + b.lost, 0);

  // Sites are child accounts: the Accounts list, its count and its export show
  // COMPANIES only (Paul #7 - 96 -> 101 after 2 accounts + 3 sites was the tell)
  const companies = useMemo(() => allAccounts.filter((a) => !a.parentAccountId), [allAccounts]);
  const filtered = useMemo(() => {
    let list = companies;
    if (search) {
      // name, vertical, industry, location, lead source + detail (A-06) and every
      // Details value on the company AND its sites - "paint" finds the plants
      // that have it (Paul #36), "trade show" finds every lead from one
      const q = search.toLowerCase();
      const words = (x) =>
        [
          x.name,
          verticalOf(x),
          x.industry,
          x.location,
          x.leadSource,
          pickField(x, 'leadSourceDetail', 'lead_source_detail'),
          ...Object.entries(x.custom || {}).flat(),
        ].filter(Boolean);
      const hay = (c) => [...words(c), ...allAccounts.filter((a) => a.parentAccountId === c.id).flatMap(words)].join(' ').toLowerCase();
      list = list.filter((c) => hay(c).includes(q));
    }
    if (tierFilter !== 'All') {
      list = list.filter((c) => normalizeTier(c.tier) === tierFilter);
    }
    if (desigFilter !== 'All') {
      list = list.filter(
        (c) => designationOf(c) === desigFilter || allAccounts.some((a) => a.parentAccountId === c.id && designationOf(a) === desigFilter)
      );
    }
    return list;
  }, [companies, allAccounts, search, tierFilter, desigFilter]);

  const handleDelete = (e, account) => {
    e.stopPropagation();
    if (!window.confirm(`Are you sure you want to delete "${account.name}"?`)) return;
    deleteAccount(account.id);
    setToast('Account deleted');
  };
  const handleEdit = (e, account) => {
    e.stopPropagation();
    setEditAccount(account);
  };

  if (selected && !selectedContact) {
    // Re-derive the live account from allAccounts so contact/activity edits
    // (which refresh the store) appear immediately instead of using the stale
    // snapshot captured at click time.
    // Keyed by id so opening a site from its company starts on a fresh panel
    // (Lead tab, no half-filled form carried over from the company).
    const liveAccount = allAccounts.find((a) => a.id === selected.id) || selected;
    return (
      <AccountDetailPanel
        key={liveAccount.id}
        account={liveAccount}
        onBack={() => {
          clearAccountFromHash();
          setSelected(null);
        }}
        backLabel={topTab === 'contacts' ? 'Back to Contacts' : 'Back to Accounts'}
        editable={editable}
        deletable={deletable}
        onOpenAccount={(a) => setSelected(a)}
      />
    );
  }

  return (
    <div>
      {/* C-01: a contact's page, from the directory or #/crm?contact=<id>. It reads
          the live contact from the store itself, so an edit or a move shows at once. */}
      {selectedContact && (
        <ContactDetailPanel
          key={selectedContact}
          contactId={String(selectedContact)}
          onBack={() => {
            clearContactFromHash();
            setSelectedContact(null);
            setTopTab('contacts');
          }}
          backLabel="Back to Contacts"
          editable={editable}
          deletable={deletable}
          onOpenAccount={(a) => {
            clearContactFromHash();
            setSelectedContact(null);
            setSelected(a);
          }}
        />
      )}
      {/* the lists stay mounted (hidden) under a contact's page, so the directory's
          search, filters, sort and any open form are all still there on Back */}
      <div hidden={!!selectedContact}>
        {toast && <Toast message={toast} onDone={() => setToast(null)} />}
        {showForm && (
          <Modal title="New Account" onClose={() => setShowForm(false)} width={660}>
            <AccountForm onClose={() => setShowForm(false)} />
          </Modal>
        )}
        {editAccount && (
          <Modal title="Edit Account" onClose={() => setEditAccount(null)} width={660}>
            <AccountForm onClose={() => setEditAccount(null)} initialData={editAccount} />
          </Modal>
        )}
        <ImportModal
          isOpen={showImportAccounts}
          onClose={() => setShowImportAccounts(false)}
          type="accounts"
          allAccounts={allAccounts}
          fetchAll={fetchAll}
        />

        {/* ===== TOP-LEVEL TABS: Accounts | Contacts ===== */}
        <div style={{ display: 'flex', borderBottom: '1px solid #ECECEC', marginBottom: 20 }}>
          {[
            ['accounts', 'Accounts'],
            ['contacts', 'Contacts'],
          ].map(([id, label]) => (
            <div
              key={id}
              onClick={() => setTopTab(id)}
              style={{
                padding: '12px 24px',
                fontSize: 13,
                fontWeight: 700,
                cursor: 'pointer',
                borderBottom: `2px solid ${topTab === id ? '#1B5E20' : 'transparent'}`,
                color: topTab === id ? '#1B5E20' : '#aaa',
                fontFamily: 'Montserrat',
              }}
            >
              {label}
            </div>
          ))}
        </div>

        {/* ===== ACCOUNTS TAB ===== */}
        {topTab === 'accounts' && (
          <div>
            <div className="kpi-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 12, marginBottom: 16 }}>
              {[
                { label: 'Total Accounts', value: companies.length, color: '#0D0D0D', bg: '#fff' },
                { label: 'Won', value: totalWon, color: '#1B5E20', bg: '#E8F5E9' },
                { label: 'Open', value: totalOpen, color: '#1565C0', bg: '#E3F2FD' },
                { label: 'Lost', value: totalLost, color: '#B71C1C', bg: '#FFEBEE' },
              ].map((s, i) => (
                <div
                  key={i}
                  style={{
                    background: s.bg,
                    borderRadius: 12,
                    padding: '16px 20px',
                    border: '1px solid #ECECEC',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 9.5,
                      fontWeight: 700,
                      color: s.color,
                      letterSpacing: 1.5,
                      textTransform: 'uppercase',
                      marginBottom: 6,
                      opacity: 0.7,
                    }}
                  >
                    {s.label}
                  </div>
                  <div style={{ fontSize: 26, fontWeight: 800, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 8, color: s.color, opacity: 0.5, marginTop: 2 }}>All Time</div>
                </div>
              ))}
            </div>

            <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #ECECEC', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
              <div style={{ padding: '16px 22px', borderBottom: '1px solid #F0F0F0' }}>
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: 12,
                    flexWrap: 'wrap',
                    gap: 8,
                  }}
                >
                  <div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#0D0D0D' }}>Accounts</span>
                    <span style={{ marginLeft: 8, fontSize: 10, color: '#aaa' }}>
                      Won / Open / Lost counted from the opportunities in GlobalOps
                    </span>
                  </div>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {/* Sync Stats button removed — KPIs are now live-derived from projects on every render */}
                    {/* Edit on Accounts or Opportunities (it checks itself) */}
                    <AiCreateButton mode="accounts" />
                    {editable && (
                      <button
                        onClick={() => setShowImportAccounts(true)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '6px 14px',
                          borderRadius: 6,
                          border: '1px solid #1B5E20',
                          background: '#fff',
                          color: '#1B5E20',
                          fontSize: 11,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: 'Montserrat',
                        }}
                      >
                        <Icon name="upload" size={11} color="#1B5E20" /> Import Accounts
                      </button>
                    )}
                    {editable && (
                      <button
                        onClick={() => setShowForm(true)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 6,
                          padding: '8px 16px',
                          borderRadius: 8,
                          border: 'none',
                          background: 'linear-gradient(135deg, #1B5E20, #2E7D32)',
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#fff',
                          cursor: 'pointer',
                          fontFamily: 'Montserrat',
                          boxShadow: '0 2px 8px rgba(27,94,32,0.25)',
                        }}
                      >
                        <Icon name="plus" size={12} color="#fff" /> Add Account
                      </button>
                    )}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
                  <div style={{ flex: 1, maxWidth: 320, position: 'relative' }}>
                    <input
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      placeholder="Search name, vertical, lead source or any detail…"
                      style={{
                        width: '100%',
                        padding: '8px 12px 8px 32px',
                        borderRadius: 8,
                        border: '1px solid #E8E8E8',
                        fontSize: 11,
                        fontFamily: 'Montserrat',
                        outline: 'none',
                        boxSizing: 'border-box',
                      }}
                    />
                    <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}>
                      <Icon name="filter" size={12} color="#ccc" />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {TIERS.map((t) => (
                      <button
                        key={t}
                        onClick={() => setTierFilter(t)}
                        style={{
                          padding: '5px 12px',
                          borderRadius: 20,
                          fontSize: 10,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: 'Montserrat',
                          border: `1px solid ${tierFilter === t ? '#1B5E20' : '#E8E8E8'}`,
                          background: tierFilter === t ? '#1B5E20' : '#fff',
                          color: tierFilter === t ? '#fff' : '#666',
                        }}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }} role="group" aria-label="Designation">
                    {['All', ...DESIGNATIONS].map((d) => (
                      <button
                        key={d}
                        onClick={() => setDesigFilter(d)}
                        aria-pressed={desigFilter === d}
                        title={DESIGNATION_DEFINITIONS[d] || 'Every designation'}
                        style={{
                          padding: '5px 12px',
                          borderRadius: 20,
                          fontSize: 10,
                          fontWeight: 700,
                          cursor: 'pointer',
                          fontFamily: 'Montserrat',
                          border: `1px solid ${desigFilter === d ? '#455A64' : '#E8E8E8'}`,
                          background: desigFilter === d ? '#455A64' : '#fff',
                          color: desigFilter === d ? '#fff' : '#666',
                        }}
                      >
                        {d === 'All' ? 'All designations' : d}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {filtered.map((c, i) => {
                const cs = getStats(c);
                return (
                  <div
                    key={c.id || i}
                    onClick={() => setSelected(c)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 16,
                      padding: '15px 22px',
                      borderBottom: '1px solid #F7F7F7',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      borderLeft: hoveredIdx === i ? '3px solid #1B5E20' : '3px solid transparent',
                      background: hoveredIdx === i ? '#FAFBFC' : 'transparent',
                    }}
                    onMouseEnter={() => setHoveredIdx(i)}
                    onMouseLeave={() => setHoveredIdx(null)}
                  >
                    <div style={{ position: 'relative' }}>
                      <div
                        style={{
                          width: 42,
                          height: 42,
                          borderRadius: 10,
                          background: '#0D0D0D',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#43A047',
                          fontSize: 12,
                          fontWeight: 800,
                          flexShrink: 0,
                        }}
                      >
                        {c.initials}
                      </div>
                      {c.isUserCreated && (
                        <div
                          style={{
                            position: 'absolute',
                            top: -2,
                            right: -2,
                            width: 8,
                            height: 8,
                            borderRadius: '50%',
                            background: '#43A047',
                            border: '2px solid #fff',
                          }}
                        />
                      )}
                    </div>
                    <div style={{ width: 180 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {(() => {
                          const won = cs.won;
                          const lost = cs.lost;
                          const open = cs.open;
                          const closed = won + lost;
                          let dotColor;
                          if (closed === 0 && open === 0) dotColor = '#C62828';
                          else if (closed === 0) dotColor = '#FFA000';
                          else {
                            const ratio = won / closed;
                            if (ratio > 0.6) dotColor = '#43A047';
                            else if (ratio >= 0.4) dotColor = '#FFA000';
                            else dotColor = '#C62828';
                          }
                          return (
                            <div
                              style={{ width: 8, height: 8, borderRadius: '50%', background: dotColor, flexShrink: 0 }}
                              title={closed > 0 ? `Win rate: ${Math.round((won / closed) * 100)}%` : 'No closed deals'}
                            />
                          );
                        })()}
                        <span style={{ fontSize: 13, fontWeight: 700, color: '#0D0D0D' }}>{c.name}</span>
                        {c.needsDetails && <NeedsDetailsChip />}
                        <NotTrackedChip account={c} />
                      </div>
                      <div style={{ fontSize: 10.5, color: '#aaa', marginTop: 2, paddingLeft: 14 }}>{c.industry}</div>
                    </div>
                    {/* Lead / Account / Customer (A-01): Customer as soon as any of its sites is one */}
                    <span style={designationTag(designationOf(c))} title="Designation">
                      {designationOf(c)}
                    </span>
                    {normalizeTier(c.tier) && (
                      <div style={{ marginRight: 8 }}>
                        <Pill label={`Tier ${normalizeTier(c.tier)}`} />
                      </div>
                    )}
                    <div style={{ flex: 1, display: 'flex', gap: 16, fontSize: 11, color: '#666' }}>
                      {c.location && c.location !== '-' && (
                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Icon name="mappin" size={11} color="#bbb" />
                          {c.location}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginRight: 6 }}>
                      {[
                        { label: 'Won', value: cs.won, bg: '#E8F5E9', color: '#1B5E20' },
                        { label: 'Open', value: cs.open, bg: '#E3F2FD', color: '#1565C0' },
                        { label: 'Lost', value: cs.lost, bg: '#FFEBEE', color: '#B71C1C' },
                      ].map(
                        (s) =>
                          s.value > 0 && (
                            <div
                              key={s.label}
                              style={{ background: s.bg, borderRadius: 8, padding: '5px 10px', textAlign: 'center', minWidth: 42 }}
                            >
                              <div style={{ fontSize: 13, fontWeight: 800, color: s.color }}>{s.value}</div>
                              <div style={{ fontSize: 8.5, fontWeight: 700, color: s.color, opacity: 0.7 }}>{s.label}</div>
                            </div>
                          )
                      )}
                    </div>
                    {editable && (
                      <button
                        onClick={(e) => handleEdit(e, c)}
                        title="Edit"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', padding: 4 }}
                      >
                        <Icon name="edit" size={14} color="#666" />
                      </button>
                    )}
                    {deletable && (
                      <button
                        onClick={(e) => handleDelete(e, c)}
                        title="Delete"
                        style={{ ...BTN_DELETE, padding: '4px 8px', fontSize: 10 }}
                      >
                        <Icon name="trash" size={12} color="#B71C1C" />
                      </button>
                    )}
                    <Icon name="chevron" size={14} color="#ccc" />
                  </div>
                );
              })}
              {filtered.length === 0 && (
                <div style={{ textAlign: 'center', padding: 40, color: '#ccc', fontSize: 11 }}>No accounts match your search</div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={() => {
                  // one row per company AND per site (Parent Account names the hierarchy), all Phase 1 fields, Details flattened (Paul #50).
                  // A-09 adds Designation (effective), Status and Lead Source Detail; Vertical is the effective one (a site uses its company's).
                  const row = (c, parent) => {
                    const s = getStats(c);
                    const clean = (v) => (v == null || v === '-' ? '' : v);
                    const hasSites = !parent && (c.siteCount || 0) > 0;
                    return {
                      Name: c.name,
                      'Parent Account': parent ? parent.name : '',
                      Type: parent ? 'Site' : 'Company',
                      Designation: designationOf(c),
                      Status: hasSites ? '' : leadStatusOf(c),
                      Vertical: clean(verticalOf(c)),
                      Industry: clean(c.industry),
                      Tier: normalizeTier(parent ? parent.tier : c.tier),
                      'Target Score': clean(c.targetScore ?? c.target_score),
                      Stage: clean(c.stageEffective || c.stage),
                      'Days in Stage': daysInStage(c) ?? '',
                      Owner: clean(c.owner),
                      'Lead Source': clean(c.leadSource),
                      'Lead Source Detail': clean(pickField(c, 'leadSourceDetail', 'lead_source_detail')),
                      'NDA Status': clean(c.ndaStatus),
                      'NDA Sent': fmtDate(c.ndaSent),
                      'NDA Executed': fmtDate(c.ndaExecuted),
                      'NDA Expires': fmtDate(c.ndaExpires),
                      'Robot Brands': (c.robotBrands || []).join('; '),
                      Sites: parent ? '' : (c.siteCount ?? 0),
                      Location: clean(c.location),
                      Phone: clean(c.phone),
                      Website: clean(c.website),
                      Won: s.won,
                      Open: s.open,
                      Lost: s.lost,
                      ...Object.fromEntries(Object.entries(c.custom || {}).map(([k, v]) => ['Detail: ' + k, String(v)])),
                    };
                  };
                  const rows = filtered.flatMap((c) => [
                    row(c, null),
                    ...allAccounts.filter((a) => a.parentAccountId === c.id).map((x) => row(x, c)),
                  ]);
                  const cols = [...new Set(rows.flatMap((r) => Object.keys(r)))];
                  exportCsv(
                    'accounts.csv',
                    rows.map((r) => Object.fromEntries(cols.map((k) => [k, r[k] ?? ''])))
                  );
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  padding: '8px 16px',
                  borderRadius: 8,
                  border: '1px solid #E8E8E8',
                  background: '#fff',
                  fontSize: 11,
                  fontWeight: 600,
                  color: '#666',
                  cursor: 'pointer',
                  fontFamily: 'Montserrat',
                }}
              >
                <Icon name="download" size={13} color="#666" /> Export Accounts
              </button>
            </div>
          </div>
        )}

        {/* ===== CONTACTS TAB ===== */}
        {topTab === 'contacts' && (
          <ContactsDirectory editable={editable} deletable={deletable} onOpenContact={(c) => setSelectedContact(String(c.id))} />
        )}
      </div>
    </div>
  );
}
